import { AdobeIMS } from "@identity/imslib";
import { IEnvironment } from "@identity/imslib/adobe-id/IEnvironment";

import { store } from "../store";

const FORCE_IMS_ACCOUNT_PICKER_KEY = "bestory_force_sign_in";

export function markNextImsSignInForceAccountPicker() {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(FORCE_IMS_ACCOUNT_PICKER_KEY, "true");
  }
}

class Deferred {
  constructor() {
    this._promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }

  promise() {
    return this._promise;
  }

  resolve(value) {
    this._resolve(value);
  }

  reject(error) {
    this._reject(error);
  }
}

/** @typedef {{ clientId: string; scope: string; environment: 'stage' | 'prod' }} IMSConfig */

/** @typedef {{ id: string; email: string; fullName: string; firstName: string; lastName: string; ownerOrg?: string | null }} IMSUserProfile */

const IMS_PROFILE_URL = "https://ims-na1.adobelogin.com/ims/profile/v1";

/**
 * @returns {string}
 */
function getImsOAuthRedirectUri() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.origin;
}

/**
 * Route to restore after IMS OAuth
 * @param {string | undefined} [explicit]
 * @returns {string}
 */
function resolveReturnPathForSignIn(explicit) {
  if (typeof explicit === "string" && explicit.length > 0) {
    return explicit;
  }
  if (typeof window === "undefined") {
    return "";
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export class IMSStore {
  constructor() {
    /** @type {AdobeIMS | undefined} */
    this._adobeIMS = undefined;
    /** @type {string | undefined} */
    this._explicitIMSToken = undefined;
    /** @type {IMSUserProfile | undefined} */
    this._userProfile = undefined;
    /** @type {Deferred<void> | undefined} */
    this._signInDef = undefined;
  }

  /**
   * @param {IMSConfig} config
   */
  async initialize(config) {
    if (this._adobeIMS) {
      return;
    }

    const imsConfig = {
      locale: "en_US",
      environment:
        config.environment === "prod" ? IEnvironment.PROD : IEnvironment.STAGE,
      scope: config.scope,
      client_id: config.clientId,
      useLocalStorage: true,
      autoValidateToken: true,
      onError: null,
      onAccessToken: null,
      onAccessTokenHasExpired: null,
      onReauthAccessToken: null,
      onReady: (context) => {
        if (
          context &&
          typeof context.returnPath === "string" &&
          context.returnPath.length > 0
        ) {
          store.getState().setRedirectPath(context.returnPath);
        }
      },
      redirect_uri: getImsOAuthRedirectUri(),
    };

    if (typeof window !== "undefined") {
      window.adobeid = imsConfig;
    }

    this._adobeIMS = new AdobeIMS(imsConfig);

    await this._adobeIMS.initialize();
  }

  _ensureReady() {
    if (!this._adobeIMS) {
      throw new Error("IMS is not initialized, call initialize first");
    }
  }

  _ensureSignIn() {
    this._ensureReady();
    if (!this._signInDef) {
      this._signInDef = new Deferred();
      const returnPath = resolveReturnPathForSignIn();
      this._adobeIMS
        .signIn(
          {
            redirect_uri: getImsOAuthRedirectUri(),
          },
          returnPath ? { returnPath } : undefined,
        )
        .then(() => {
          this._signInDef?.resolve();
        })
        .catch((error) => {
          this._signInDef?.reject(error);
        });
    }
    return this._signInDef.promise();
  }

  get isSignedIn() {
    return this._adobeIMS?.isSignedInUser() ?? false;
  }

  /** Cached profile from last getUserProfile (example-style). */
  get userProfile() {
    return this._userProfile;
  }

  /**
   * @param {string} token
   */
  setExplicitIMSToken(token) {
    this._explicitIMSToken = token;
    this._userProfile = undefined;
  }

  async getAccessToken() {
    if (this._explicitIMSToken) {
      return this._explicitIMSToken;
    }

    this._ensureReady();

    if (!this._adobeIMS.isSignedInUser()) {
      await this._ensureSignIn();
    }

    return this._adobeIMS.getAccessToken()?.token ?? null;
  }

  /**
   * @returns {Promise<boolean>} true if imslib refreshed and a new access token is available
   */
  async refreshAccessToken() {
    if (this._explicitIMSToken) {
      return false;
    }
    try {
      this._ensureReady();
    } catch {
      return false;
    }
    if (!this._adobeIMS.isSignedInUser()) {
      return false;
    }
    try {
      await this._adobeIMS.refreshToken({});
      this._userProfile = undefined;
      const token = this._adobeIMS.getAccessToken()?.token ?? null;
      if (token) {
        store.getState().setToken(token);
      }
      return !!token;
    } catch {
      return false;
    }
  }

  /**
   * @returns {Promise<IMSUserProfile>}
   */
  async getUserProfile() {
    if (this._userProfile) {
      return this._userProfile;
    }

    if (this._explicitIMSToken) {
      const res = await fetch(IMS_PROFILE_URL, {
        headers: { Authorization: `Bearer ${this._explicitIMSToken}` },
      });
      if (!res.ok) {
        throw new Error(`401: profile request failed (${res.status})`);
      }
      const data = await res.json();
      this._userProfile = {
        id: data.userId,
        email: data.email,
        fullName: data.name,
        firstName: data.first_name,
        lastName: data.last_name,
        ownerOrg: data.ownerOrg ?? null,
        ldap: data.email?.split("@")?.[0] ?? null,
      };
      return this._userProfile;
    }

    this._ensureReady();

    if (!this._adobeIMS.isSignedInUser()) {
      await this._ensureSignIn();
    }

    const imsProfile = await this._adobeIMS.getProfile();

    this._userProfile = {
      id: imsProfile.userId,
      email: imsProfile.email,
      fullName: imsProfile.name,
      firstName: imsProfile.first_name,
      lastName: imsProfile.last_name,
      ownerOrg: imsProfile.ownerOrg ?? null,
      ldap: imsProfile?.email?.split("@")?.[0] ?? null,
    };

    return this._userProfile;
  }

  /**
   * @param {boolean} [forceSignIn]
   * @param {string} [returnPath] App route to restore (pathname+search+hash); forwarded in IMS OAuth state.context
   */
  async signIn(forceSignIn = false, returnPath) {
    this._ensureReady();
    let force = !!forceSignIn;
    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem(FORCE_IMS_ACCOUNT_PICKER_KEY) === "true") {
        sessionStorage.removeItem(FORCE_IMS_ACCOUNT_PICKER_KEY);
        force = true;
      }
    }
    if (!force && this._adobeIMS.isSignedInUser()) {
      return;
    }
    const options = {
      redirect_uri: getImsOAuthRedirectUri(),
    };
    if (force) {
      options.force_sign_in = true;
    }
    const path = resolveReturnPathForSignIn(returnPath);
    await this._adobeIMS.signIn(
      options,
      path ? { returnPath: path } : undefined,
    );
  }

  async signUp() {
    this._ensureReady();
    const returnPath = resolveReturnPathForSignIn();
    await this._adobeIMS.signUp(
      {
        redirect_uri: getImsOAuthRedirectUri(),
      },
      returnPath ? { returnPath } : undefined,
    );
  }

  getSSORedirectHref() {
    const redirectURL = new URL(window.location.origin);
    return redirectURL.toString();
  }

  async signOut() {
    this._ensureReady();
    this._userProfile = undefined;
    this._explicitIMSToken = undefined;
    this._signInDef = undefined;
    store.getState().clearIms();
    this._adobeIMS.signOut({
      redirect_uri: this.getSSORedirectHref(),
    });
  }
}

export const IMS = new IMSStore();
